import { Box, Typography, Popover } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { FlexCenVer, FlexCol } from '@/UI/styles';
import { useIsHovered } from '@/hooks/useIsHovered';
import {
  CopyButton,
  CopyIndication,
  CustomChip,
  ExpandableText,
  ParamDisplay,
  SortedParams,
} from './ParamsDisplayComponents';
import { extractValue, processInputs, type ProcessedInputs } from './galleryParamsUtils';
import ReferenceImages from './ReferenceImages';

const ANCHOR_ORIGIN = {
  vertical: 'bottom',
  horizontal: 'left',
} as const;

const POPOVER_STYLES = {
  '& .MuiPopover-paper': { width: '400px' },
};

const EMPTY_OBJECT = {};

interface GalleryParamsProps {
  inputs: Record<string, unknown>;
  container?: string;
}

function GalleryParams({ inputs, container = '' }: GalleryParamsProps) {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const { isHovered: isPromptHovered, onMouseEnter, onMouseLeave } = useIsHovered();
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const handleClickOpenParams = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
    track('gallery_params_opened', {}, TrackTypeEnum.Product);
  };

  const handleCloseParams = useCallback(() => {
    track('gallery_params_closed', {}, TrackTypeEnum.Product);
    setAnchorEl(null);
  }, [track]);

  const open = Boolean(anchorEl);
  const id = open ? 'params-popover' : undefined;

  const { sortedParams, prompt, negativePrompt, imageInputs } = useMemo<Partial<ProcessedInputs>>(() => {
    if (inputs) {
      return processInputs(inputs);
    } else {
      return {
        sortedParams: undefined,
        prompt: undefined,
        negativePrompt: undefined,
        imageInputs: undefined,
      };
    }
  }, [inputs]);

  const { copyToClipboard: copyPromptToClipboard, copied: promptCopied } = useCopyToClipboard({
    trackingName: 'gallery_params_copied_prompt',
    trackingPayload: EMPTY_OBJECT,
  });
  const { copyToClipboard: copyNegativePromptToClipboard, copied: negativePromptCopied } = useCopyToClipboard({
    trackingName: 'gallery_params_copied_negative_prompt',
    trackingPayload: EMPTY_OBJECT,
  });
  const promptValue = extractValue(prompt);
  const negativePromptValue = extractValue(negativePrompt);

  if (container === 'gallery') {
    return (
      <FlexCol sx={{ gap: 1 }}>
        <Box onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} sx={{ mb: 2 }}>
          <FlexCenVer sx={{ justifyContent: 'space-between', height: '24px' }}>
            {promptValue && (
              <Typography variant="body-sm-rg">{t(I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.PARAMS.PROMPT)}</Typography>
            )}
            {isPromptHovered && promptValue && (
              <CopyButton text={promptValue} trackingName="gallery_params_copied_prompt" size="medium" />
            )}
          </FlexCenVer>
          <Typography variant="body-sm-rg" sx={{ mb: 2 }} color={color.White64_T}>
            {promptValue}
          </Typography>
        </Box>
        {imageInputs && imageInputs.length > 0 && (
          <ReferenceImages
            imageInputs={imageInputs}
            label={t(I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.PARAMS.REFERENCE_IMAGE, { count: imageInputs.length })}
          />
        )}
        <SortedParams params={sortedParams} RenderComponent={ParamDisplay} />
      </FlexCol>
    );
  }
  return (
    <>
      <Typography
        onClick={handleClickOpenParams}
        variant="body-sm-rg"
        sx={{
          width: 'fit-content',
          p: 1,
          ml: -1,
          cursor: 'pointer',
          gap: 0.5,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <i className={`fa-light fa-circle-${open ? 'x' : 'plus'} fa-lg`}></i>
        {t(
          open
            ? I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.PARAMS.HIDE_INFO
            : I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.PARAMS.SHOW_INFO,
        )}
      </Typography>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseParams}
        anchorOrigin={ANCHOR_ORIGIN}
        sx={POPOVER_STYLES}
        disableAutoFocus
        disableEnforceFocus
      >
        <FlexCol sx={{ background: color.Black100, p: 2, gap: 1 }}>
          {promptValue && (
            <Box id="gallery-param-prompt" sx={{ position: 'relative' }}>
              <Typography variant="body-std-md" sx={{ mb: 1 }}>
                {t(I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.PARAMS.PROMPT)}
              </Typography>
              <ExpandableText text={promptValue} />
              {/* support both prompt and prompt.value coming from LLM results */}
              <CopyIndication
                copied={promptCopied}
                copyToClipboard={() => promptValue && void copyPromptToClipboard(promptValue)}
              />
            </Box>
          )}
          {negativePromptValue && (
            <Box id="gallery-param-n-prompt" sx={{ position: 'relative' }}>
              <Typography variant="body-std-md" sx={{ my: 1 }}>
                {t(I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.PARAMS.NEGATIVE_PROMPT)}
              </Typography>
              <ExpandableText text={negativePromptValue} />
              <CopyIndication
                copied={negativePromptCopied}
                copyToClipboard={() => negativePromptValue && void copyNegativePromptToClipboard(negativePromptValue)}
              />
            </Box>
          )}
          <SortedParams
            params={sortedParams}
            RenderComponent={CustomChip}
            header={
              <Typography variant="body-std-md" sx={{ my: 1 }}>
                {t(I18N_KEYS.RECIPE_MAIN.FLOW.GALLERY.PARAMS.PARAMETERS)}
              </Typography>
            }
          />
        </FlexCol>
      </Popover>
    </>
  );
}

export default GalleryParams;
